import React from 'react';
import { Link } from 'react-router-dom';
import {
  StyledPostTitle,
  StyledParagraph,
  StyledSectionHeading,
  StyledOL,
} from '../styled';

const thStyle = {
  padding: '8px 12px 8px 0',
  borderBottom: '1px solid #000',
  verticalAlign: 'bottom',
};
const thLastStyle = { ...thStyle, padding: '8px 0' };
const rowHeadStyle = {
  padding: '10px 12px 10px 0',
  verticalAlign: 'top',
  fontWeight: 500,
};
const tdStyle = { padding: '10px 12px 10px 0', verticalAlign: 'top' };
const tdLastStyle = { padding: '10px 0', verticalAlign: 'top' };

const COMPARISON_ROWS = [
  [
    'Made by',
    'Gamebasics (part of Miniclip since 2021)',
    'Squiggly Games',
  ],
  [
    'Where you play',
    'Android and iOS apps, and the OSM website',
    'In the browser (phone or desktop); Steam is optional',
  ],
  [
    'Clubs',
    'Real leagues, clubs and players',
    'TOL’s own clubs: servers of 30, split into three leagues of ten',
  ],
  [
    'Who you play',
    'Real managers; clubs nobody takes are run by the computer',
    'Real managers; a club with no manager is bot-run until claimed',
  ],
  [
    'After the season',
    'You pick a new team (one-season game)',
    'Your club carries on after a 3-day off-season with a youth camp',
  ],
  [
    'Match timing',
    'Set by your league’s server timer',
    'A league match every night at 7 pm server time, cup games at noon on most days',
  ],
  [
    'Transfers',
    'Scout, transfer list and negotiations',
    'Agree a deal with the other manager, then send the offer in the portal',
  ],
  [
    'Where the social side happens',
    'In the app',
    'In your league’s Discord',
  ],
  [
    'Price',
    'Free, with ads and in-app purchases',
    'Free to play, optional paid Patron extras',
  ],
];

export default function Article() {
  return (
    <>
      <StyledPostTitle id="online-soccer-manager-alternative">
        Online Soccer Manager alternative: keep your club next season
      </StyledPostTitle>
      <StyledParagraph big>
        Online Soccer Manager (OSM) is one of the most-played football manager
        games on phones. It is made by Gamebasics, a Dutch studio that has been
        part of Miniclip since 2021, and it lets you take charge of a real club
        with real players. People who search for an Online Soccer Manager
        alternative usually still enjoy that. What they want to change is the
        reset: in OSM, when the season ends, so does your time at that club.
      </StyledParagraph>
      <StyledParagraph big>
        The Open League (TOL) is a free football manager you play in the
        browser on your phone or computer. Steam is optional. Clubs are run by
        real people (a club with no manager is run by a bot until someone
        claims it), seasons last three weeks, a league match is played every
        night at 7 pm, and the league lives in Discord. Your club carries on
        into the next season. TOL is smaller and simpler than OSM, and this
        page is honest about that.
      </StyledParagraph>

      <StyledSectionHeading>
        What OSM does well, and why people look elsewhere
      </StyledSectionHeading>
      <StyledParagraph big>
        OSM earned its audience. You can manage famous clubs with their real
        squads, it is easy to pick up, and on its store page OSM says it has a
        community of more than 50 million players in 30 languages. You can
        create a league with friends and play friendlies whenever you like.
      </StyledParagraph>
      <StyledParagraph big>
        The same design explains why some managers drift away:
      </StyledParagraph>
      <StyledParagraph big>
        <StyledOL>
          <li>
            The club resets. OSM calls itself a one-season game. When the season
            ends, you choose a new team and start again. The only exception is
            qualifying for the Winners Cup from a top league.
          </li>
          <li>
            You don’t choose when matches happen. Matchdays are simulated on a
            timer set by your league’s server, and OSM’s help pages say you
            can’t change it.
          </li>
          <li>
            Your rivals are often strangers. Unless you set up a league with
            friends, you are up against managers from all over the world.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        If none of that bothers you, keep playing OSM. If it does, read on.
      </StyledParagraph>

      <StyledSectionHeading>OSM vs The Open League at a glance</StyledSectionHeading>
      <StyledParagraph small>Facts as of October 2026.</StyledParagraph>
      <div style={{ overflowX: 'auto', marginBottom: 20 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: 'Archivo, sans-serif',
            fontWeight: 300,
            fontSize: 16,
            lineHeight: '26px',
            letterSpacing: '0.4px',
            textAlign: 'left',
          }}
        >
          <thead>
            <tr>
              <th style={thStyle} aria-label="Comparison" />
              <th style={thStyle}>Online Soccer Manager</th>
              <th style={thLastStyle}>The Open League</th>
            </tr>
          </thead>
          <tbody>
            {COMPARISON_ROWS.map(([label, osm, tol]) => (
              <tr key={label}>
                <th scope="row" style={rowHeadStyle}>
                  {label}
                </th>
                <td style={tdStyle}>{osm}</td>
                <td style={tdLastStyle}>{tol}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <StyledSectionHeading>
        Where The Open League is the better fit
      </StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            You want to keep building. A TOL season lasts three weeks, then
            comes a short off-season with a youth camp, and the same club goes
            into the next season. Promotion and relegation mean last season
            still matters. See how the{' '}
            <Link to="/user-guide/off-season">off-season</Link> works.
          </li>
          <li>
            You want a fixed match night. Every club on a server plays its
            league match at 7 pm server time, with{' '}
            <Link to="/user-guide/competitions">cup games</Link> at noon on
            most days. Your group can plan the evening around it.
          </li>
          <li>
            You want to know your rivals. Each server has 30 clubs, and you
            learn who is above and below you in the table. If you are bringing
            friends, see our guide to playing a{' '}
            <Link to="/blog/football-manager-with-friends">
              football manager with friends
            </Link>
            .
          </li>
          <li>
            You like negotiating with people. A transfer starts as a chat with
            another manager, then you make the offer official in the club
            portal. The <Link to="/user-guide/trading">trading guide</Link>{' '}
            explains how.
          </li>
          <li>
            Your group already lives in Discord. Match highlights, banter and
            deals all happen in your league’s server.
          </li>
        </StyledOL>
      </StyledParagraph>

      <StyledSectionHeading>Where OSM is still the better pick</StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Real clubs and players. If managing a club you support, with its
            real squad, is the point, OSM does that and TOL does not.
          </li>
          <li>
            Scale and languages. OSM has a huge player base and runs in 30
            languages. TOL is a small game with 30-club servers.
          </li>
          <li>
            Play whenever you open the app. OSM fits quick sessions and
            unlimited friendlies. TOL is built around a fixed 7 pm match each
            night.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        Plenty of managers will be happy playing both.
      </StyledParagraph>

      <StyledSectionHeading>Coming from Soccer Manager Worlds?</StyledSectionHeading>
      <StyledParagraph big>
        Soccer Manager Worlds is Soccer Manager’s turn-based multiplayer game,
        where managers take real clubs in shared game worlds. If what you like
        is beating people you know, TOL gives you the same rivalry in a
        shorter, three-week season, with the league in Discord.
      </StyledParagraph>

      <StyledSectionHeading>
        If OSM is not the game you are comparing
      </StyledSectionHeading>
      <StyledParagraph big>
        If you came from another mobile manager, read our{' '}
        <Link to="/blog/top-eleven-alternative">Top Eleven alternative</Link>{' '}
        page. If you came from the long-running browser game Hattrick, see our{' '}
        <Link to="/blog/hattrick-alternative">Hattrick alternative</Link> page.
        For more games like Online Soccer Manager and other guides, browse{' '}
        <Link to="/blog">the blog</Link>.
      </StyledParagraph>

      <StyledSectionHeading>How to start a season tonight</StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Open <Link to="/">The Open League</Link> in your browser and
            register or log in. Steam is optional.
          </li>
          <li>
            Pick a server whose 7 pm match time suits your evenings, and claim a
            club.
          </li>
          <li>
            Set your starting eleven and tactics, then join Discord to follow
            the match.
          </li>
          <li>
            Read <Link to="/user-guide/start">Getting started</Link> before
            your first match.
          </li>
        </StyledOL>
      </StyledParagraph>

      <StyledSectionHeading>FAQ</StyledSectionHeading>
      <StyledParagraph big>
        <strong>Is The Open League like Online Soccer Manager?</strong> Both
        are free football managers you can play on your phone against other
        people. The differences are that TOL uses its own clubs instead of real
        ones, your club carries on from season to season, and the league lives
        in Discord.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Does my team reset after a season?</strong> Not in TOL. After
        three weeks of league matches there is a three-day off-season with a
        youth camp, and then the same club starts the next season. In OSM, you
        choose a new team when the season ends.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>When are matches played?</strong> League matches are every
        night at 7 pm server time, and cup games are at noon on most days. You
        pick a server whose time suits you.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Do I need to pay to compete?</strong> No. TOL is free to play,
        and transfers are agreed between managers. The optional Patron extras
        include an extra academy spot and automatic substitutions for injured
        players.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Can I play on my phone?</strong> Yes. TOL runs in your phone’s
        browser, so you don’t need to install an app. Steam is optional if you
        prefer to play on a computer.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>What are other games like Online Soccer Manager?</strong> If you
        want the same kind of quick, social management game, see our{' '}
        <Link to="/blog/top-eleven-alternative">Top Eleven alternative</Link>{' '}
        and <Link to="/blog/hattrick-alternative">Hattrick alternative</Link>{' '}
        pages. Both compare those games with TOL.
      </StyledParagraph>

      <StyledParagraph big>
        A good OSM alternative keeps what you liked, which is managing a club
        from your phone against real people, and changes what you did not. The
        Open League gives you a club you keep, rivals you get to know, a 7 pm
        match every night, and a league you follow in Discord.
      </StyledParagraph>
      <StyledParagraph big>
        <Link to="/">Claim a club in The Open League</Link> and follow tonight’s
        7 pm match in Discord.
      </StyledParagraph>
    </>
  );
}
